import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import DataGrid, { Column, FilterRow, SearchPanel, Export, Paging, ColumnFixing } from "devextreme-react/data-grid";
import { Button } from 'devextreme-react/button';
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import notify from 'devextreme/ui/notify';
import { exportDataGrid } from 'devextreme/excel_exporter';
import saveAs from "file-saver";
import ExcelJS from "exceljs";
import API_URL from '../config';
import 'devextreme/dist/css/dx.light.css';

const REPORT_API = `${API_URL}/api/hard-bin/yield-rollup`; 
const CUST_API_URL = `${API_URL}/api/local/common/custs`;
const DEVICE_API_URL = `${API_URL}/api/local/common/devices`;

export default function HardBinYieldReport({ user }) { // user prop 추가
  const dataGridRef = useRef(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [dateTo, setDateTo] = useState(new Date());
  const [selectedFactory, setSelectedFactory] = useState('FT01');
  const [selectedCust, setSelectedCust] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [operList, setOperList] = useState([]);
  const [selectedOper, setSelectedOper] = useState(null);
  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);

  const formatDate = (date) => date ? `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, "0")}${date.getDate().toString().padStart(2, "0")}` : '';

  useEffect(() => { 
    axios.get(CUST_API_URL, { params: { factory: selectedFactory } }).then(res => setCustList(res.data.data || []));
  }, [selectedFactory]);

  // [추가] CUST 사용자 자동 선택 로직
  useEffect(() => {
    if (user && user.role === "CUST" && custList.length > 0) {
      const found = custList.find(c => c.CODE_CD.trim().toUpperCase() === user.username.trim().toUpperCase());
      if (found) {
        setSelectedCust(found.CODE_CD);
      }
    }
  }, [user, custList]);

  useEffect(() => {
    if (!selectedFactory || !selectedCust) { setDeviceList([]); setSelectedDevice(null); return; }
    axios.get(DEVICE_API_URL, { params: { factory: selectedFactory, custCd: selectedCust } }).then(res => setDeviceList([{ CODE_CD: 'ALL', CODE_NM: 'ALL' }, ...(res.data.data || [])]));
  }, [selectedFactory, selectedCust]);
  
  useEffect(() => {
    const opers = selectedFactory === 'FT01' ? ['FTST', 'FTHT', 'FTCT', 'FTST2'] : ['PTST', 'PTST2', 'PTST3', 'PTST4'];
    setOperList(['ALL', ...opers]);
    setSelectedOper('ALL');
  }, [selectedFactory]);

  const fetchData = useCallback(async () => {
    if (!selectedFactory || !selectedCust) {
      notify("Factory와 CUST는 필수 선택 항목입니다.", "warning", 2000);
      return;
    }
    setLoading(true);
    setData([]);
    try {
      const params = {
        factory: selectedFactory,
        custCd: selectedCust,
        device: selectedDevice || 'ALL',
        oper: selectedOper || 'ALL',
        dateFrom: formatDate(dateFrom),
        dateTo: formatDate(dateTo),
      };
      const response = await axios.get(REPORT_API, { params });
      if (response.data.retCode !== 0) throw new Error(response.data.retMsg);
      
      const resultData = response.data.data || [];
      const processedData = [];
      let currentMatId = null;

      resultData.forEach((row, index) => {
        if (row.GRPID === 0 && row.MAT_ID !== currentMatId) {
          currentMatId = row.MAT_ID;
          const yieldRow = {
            ID: `yield-${row.MAT_ID}-${index}`,
            isYieldRow: true,
            BIN1: row.YIELD1, BIN2: row.YIELD2, BIN3: row.YIELD3,
            BIN4: row.YIELD4, BIN5: row.YIELD5, BIN6: row.YIELD6,
            BIN7: row.YIELD7, BIN8: row.YIELD8, BIN9: row.YIELD9,
          };
          processedData.push(yieldRow);
        }
        processedData.push({ ...row, ID: index });
      });
      
      setData(processedData);
      if (resultData.length === 0) notify("조회된 데이터가 없습니다.", "info", 2000);
    } catch (e) {
      notify(`조회 오류: ${e.message}`, "error", 4000);
    } finally {
      setLoading(false);
    }
  }, [selectedFactory, selectedCust, selectedDevice, selectedOper, dateFrom, dateTo]);
  
  const onExporting = (e) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('HardBinYield');

    exportDataGrid({
        component: e.component, 
        worksheet: worksheet,
        autoFilterEnabled: true,
        customizeCell: ({ gridCell, excelCell }) => {
            if (gridCell.rowType === 'header') {
                excelCell.font = { bold: true };
                excelCell.alignment = { horizontal: 'center' };
            }
            if (gridCell.rowType === 'data') {
                if (gridCell.data.isYieldRow) { 
                    excelCell.font = { bold: true, color: { argb: 'FF1D39C4' } };
                    excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6F7FF' } };
                } else if (gridCell.data.GRPID > 0) { 
                    excelCell.font = { bold: true };
                    excelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0F0F0' } };
                }
            }
        }
    }).then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
            saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'HardBinYieldReport.xlsx');
        });
    });
    e.cancel = true;
  };

  const onCellPrepared = (e) => {
    if (e.rowType === 'data') {
      if (e.data.GRPID > 0) {
          e.cellElement.style.backgroundColor = '#f0f0f0';
          e.cellElement.style.fontWeight = 'bold';
      }
      if (e.data.isYieldRow) {
          e.cellElement.style.backgroundColor = '#e6f7ff';
          e.cellElement.style.fontWeight = 'bold';
          e.cellElement.style.color = '#1d39c4';
          if (e.column.dataField && !e.column.dataField.startsWith('BIN')) {
            e.cellElement.innerText = '';
          }
      }
    }
  };

  // [수정] CUST 사용자 확인 변수
  const isCustUser = user && user.role === "CUST";

  return (
    <div style={{ padding: 20, height: 'calc(100vh - 40px)' }}>
      {/* 검색 조건 UI */}
      <div style={{ marginBottom: '20px', padding: '20px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
        <h3 style={{ margin: '0 0 20px 0' }}>Hard Bin Yield Report</h3>
        <div style={{ display: "flex", gap: 15, flexWrap: "wrap", alignItems: 'center' }}>
            <DateBox defaultValue={dateFrom} onValueChanged={e => setDateFrom(e.value)} label="FROM" width={180} type="date" displayFormat="yyyy-MM-dd" />
            <DateBox defaultValue={dateTo} onValueChanged={e => setDateTo(e.value)} label="TO" width={180} type="date" displayFormat="yyyy-MM-dd" />
            <SelectBox items={['FT01']} value={selectedFactory} onValueChanged={e => setSelectedFactory(e.value)} label="FACTORY" width={150} />
            <SelectBox dataSource={operList} value={selectedOper} onValueChanged={e => setSelectedOper(e.value)} label="OPER" width={150} />
            
            <SelectBox 
              dataSource={custList} 
              value={selectedCust} 
              valueExpr="CODE_CD" 
              displayExpr="CODE_NM" 
              onValueChanged={e => setSelectedCust(e.value)} 
              disabled={isCustUser} // CUST 유저는 비활성화
              showClearButton={!isCustUser} // CUST 유저는 클리어 버튼 숨김
              searchEnabled 
              placeholder="Select CUST" 
              label="CUST" 
              width={220} 
            />
            
            <SelectBox dataSource={deviceList} value={selectedDevice} valueExpr="CODE_CD" displayExpr="CODE_CD" onValueChanged={e => setSelectedDevice(e.value)} searchEnabled label="DEVICE" width={220} placeholder="Select DEVICE" />
            <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
        </div>
      </div>
      
      <DataGrid
        ref={dataGridRef} 
        dataSource={data}
        keyExpr="ID"
        onExporting={onExporting}
        onCellPrepared={onCellPrepared}
        height="calc(100% - 150px)"
        showBorders={true}
        allowColumnResizing={true}
        columnAutoWidth={true}
        loadPanel={{ enabled: loading }}
      >
        <Paging defaultPageSize={100} /> <FilterRow visible={true} /> <SearchPanel visible={true} /> <ColumnFixing enabled={true} /> <Export enabled={true} />
        
        <Column dataField="CUSTOMER" width={120} fixed={true} />
        <Column dataField="MAT_ID" caption="MAT_ID" width={110} fixed={true} />
        <Column dataField="LOT_ID" width={140} fixed={true} />
        <Column dataField="TRAN_TIME" width={150} />
        <Column dataField="OPER" width={80} />
        <Column dataField="TEST_TIME" width={90} format={{type: 'fixedPoint', precision: 2}}/>
        <Column dataField="PARA" width={70} /> 
        <Column dataField="INDEX_TIME" width={100} /> 
        <Column dataField="START_RES_ID" width={120} />
        <Column dataField="IN_QTY" width={90} alignment="right" /> 
        <Column dataField="OUT_QTY" width={90} alignment="right" /> 
        <Column dataField="R_QTY" width={90} alignment="right" />
        
        <Column dataField="BIN1" caption="BIN1" width={80} alignment="right" format={{type: 'fixedPoint', precision: 2}} />
        <Column dataField="BIN2" caption="BIN2" width={80} alignment="right" format={{type: 'fixedPoint', precision: 2}} />
        <Column dataField="BIN3" caption="BIN3" width={80} alignment="right" format={{type: 'fixedPoint', precision: 2}} />
        <Column dataField="BIN4" caption="BIN4" width={80} alignment="right" format={{type: 'fixedPoint', precision: 2}} />
        <Column dataField="BIN5" caption="BIN5" width={80} alignment="right" format={{type: 'fixedPoint', precision: 2}} />
        <Column dataField="BIN6" caption="BIN6" width={80} alignment="right" format={{type: 'fixedPoint', precision: 2}} />
        <Column dataField="BIN7" caption="BIN7" width={80} alignment="right" format={{type: 'fixedPoint', precision: 2}} />
        <Column dataField="BIN8" caption="BIN8" width={80} alignment="right" format={{type: 'fixedPoint', precision: 2}} />
        <Column dataField="BIN9" caption="BIN9" width={80} alignment="right" format={{type: 'fixedPoint', precision: 2}} />
        
        <Column dataField="PGM" caption="PGM" width={250} />
        <Column dataField="GRPID" visible={false} />
        <Column dataField="isYieldRow" visible={false} />
      </DataGrid>
    </div>
  );
}